import { useCallback, useMemo, useState } from "react";
import Defaults from "../../helper/defaults";
import { buildSearchQuery } from "../../helper/searchQuery";
import { areSearchQueriesEquivalent, isSaveSearchDisabled } from "../../helper/savedSearchDirty";
import { extractWorkspaceSavedQuery, filterStateFromSavedQuery } from "./savedSearchQuery";

function useSavedSearchFilters({
    preferWorkspaceSnapshotRef
}) {
const [originalSavedFilters, setOriginalSavedFilters] = useState(null);

const [termsInclude, setTermsInclude] = useState(Defaults.search.termsInclude);

const [termsExclude, setTermsExclude] = useState(Defaults.search.termsExclude);

const [industryInclude, setIndustryInclude] = useState(Defaults.search.industryInclude);

const [industryExclude, setIndustryExclude] = useState(Defaults.search.industryExclude);

const [locations, setLocations] = useState(Defaults.search.locations);

const [revenueLowerBound, setRevenueLowerBound] = useState(Defaults.search.revenueLowerBound);

const [revenueUpperBound, setRevenueUpperBound] = useState(Defaults.search.revenueUpperBound);

const [employeesLowerBound, setEmployeesLowerBound] = useState(Defaults.search.employeesLowerBound);

const [employeesUpperBound, setEmployeesUpperBound] = useState(Defaults.search.employeesUpperBound);

const [naturalQuery, setNaturalQuery] = useState(Defaults.search.naturalLanguageQuery);

const [filtersHydrated, setFiltersHydrated] = useState(false);

const hasNaturalQuery = Boolean((naturalQuery || "").trim());

const hydrateFiltersFromWorkspace = useCallback((detail) => {
    const savedQuery = extractWorkspaceSavedQuery(detail);
    const next = filterStateFromSavedQuery(savedQuery);
    setTermsInclude(next.termsInclude);
    setTermsExclude(next.termsExclude);
    setIndustryInclude(next.industryInclude);
    setIndustryExclude(next.industryExclude);
    setLocations(next.locations);
    setRevenueLowerBound(next.revenueLowerBound);
    setRevenueUpperBound(next.revenueUpperBound);
    setEmployeesLowerBound(next.employeesLowerBound);
    setEmployeesUpperBound(next.employeesUpperBound);
    setNaturalQuery(next.naturalQuery);
    setOriginalSavedFilters(savedQuery && typeof savedQuery === "object" ? savedQuery : null);
    setFiltersHydrated(true);
  }, []);

const unlockLiveSearch = useCallback(() => {
    preferWorkspaceSnapshotRef.current = false;
  }, [preferWorkspaceSnapshotRef]);

const setTermsIncludeLive = useCallback((value) => {
    unlockLiveSearch();
    setTermsInclude(value);
  }, [unlockLiveSearch]);

const setTermsExcludeLive = useCallback((value) => {
    unlockLiveSearch();
    setTermsExclude(value);
  }, [unlockLiveSearch]);

const setIndustryIncludeLive = useCallback((value) => {
    unlockLiveSearch();
    setIndustryInclude(value);
  }, [unlockLiveSearch]);

const setIndustryExcludeLive = useCallback((value) => {
    unlockLiveSearch();
    setIndustryExclude(value);
  }, [unlockLiveSearch]);

const setLocationsLive = useCallback((value) => {
    unlockLiveSearch();
    setLocations(value);
  }, [unlockLiveSearch]);

const setRevenueLowerBoundLive = useCallback((value) => {
    unlockLiveSearch();
    setRevenueLowerBound(value);
  }, [unlockLiveSearch]);

const setRevenueUpperBoundLive = useCallback((value) => {
    unlockLiveSearch();
    setRevenueUpperBound(value);
  }, [unlockLiveSearch]);

const setEmployeesLowerBoundLive = useCallback((value) => {
    unlockLiveSearch();
    setEmployeesLowerBound(value);
  }, [unlockLiveSearch]);

const setEmployeesUpperBoundLive = useCallback((value) => {
    unlockLiveSearch();
    setEmployeesUpperBound(value);
  }, [unlockLiveSearch]);

const setNaturalQueryLive = useCallback((value) => {
    unlockLiveSearch();
    setNaturalQuery(value);
  }, [unlockLiveSearch]);

const activeQuery = useMemo(
    () =>
      buildSearchQuery({
        termsInclude,
        termsExclude,
        industryInclude,
        industryExclude,
        locations,
        revenueLowerBound,
        revenueUpperBound,
        employeesLowerBound,
        employeesUpperBound,
        naturalQuery,
        hasNaturalQuery,
      }),
    [
      termsInclude,
      termsExclude,
      industryInclude,
      industryExclude,
      locations,
      revenueLowerBound,
      revenueUpperBound,
      employeesLowerBound,
      employeesUpperBound,
      naturalQuery,
      hasNaturalQuery,
    ]
  );

const isSavedSearchDirty = useMemo(() => {
    if (!originalSavedFilters) return false;
    return !areSearchQueriesEquivalent(originalSavedFilters, activeQuery);
  }, [activeQuery, originalSavedFilters]);

const isStandardFilterEmpty = useMemo(
    () =>
      locations.length === 0 &&
      termsInclude.length === 0 &&
      termsExclude.length === 0 &&
      industryInclude.length === 0 &&
      industryExclude.length === 0 &&
      (revenueLowerBound === null || revenueLowerBound === Defaults.search.revenueLowerBound) &&
      (revenueUpperBound === null || revenueUpperBound === Defaults.search.revenueUpperBound) &&
      (employeesLowerBound === null ||
        employeesLowerBound === Defaults.search.employeesLowerBound) &&
      (employeesUpperBound === null ||
        employeesUpperBound === Defaults.search.employeesUpperBound),
    [
      locations,
      termsInclude,
      termsExclude,
      industryInclude,
      industryExclude,
      revenueLowerBound,
      revenueUpperBound,
      employeesLowerBound,
      employeesUpperBound,
    ]
  );

const isSearchActive = useMemo(
    () => !isStandardFilterEmpty || hasNaturalQuery,
    [hasNaturalQuery, isStandardFilterEmpty]
  );

const saveSearchDisabled = useMemo(
    () =>
      isSaveSearchDisabled({
        isSearchActive,
        hasSavedBaseline: Boolean(originalSavedFilters),
        isDirty: isSavedSearchDirty,
        requireDirtyWhenSaved: true,
      }),
    [isSavedSearchDirty, isSearchActive, originalSavedFilters]
  );

return {
    originalSavedFilters, setOriginalSavedFilters, termsInclude, termsExclude,
    industryInclude, industryExclude, locations, revenueLowerBound,
    revenueUpperBound, employeesLowerBound, employeesUpperBound, naturalQuery,
    filtersHydrated, setFiltersHydrated, hasNaturalQuery, hydrateFiltersFromWorkspace,
    unlockLiveSearch, setTermsIncludeLive, setTermsExcludeLive, setIndustryIncludeLive,
    setIndustryExcludeLive, setLocationsLive, setRevenueLowerBoundLive, setRevenueUpperBoundLive,
    setEmployeesLowerBoundLive, setEmployeesUpperBoundLive, setNaturalQueryLive, activeQuery,
    isSavedSearchDirty, saveSearchDisabled
};
}

export { useSavedSearchFilters };
